import React, { useContext, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { connect } from 'react-redux'
import styled, { createGlobalStyle } from 'styled-components'
import Favico from 'favico.js'

import GlobalAlert from 'live/components/ui/global-alert'
import { DashboardContext } from 'live/context/dashboard'
import { LiveApi } from 'live/lib/api'

import active from '../../../sounds/reminder.wav'
import critical from '../../../sounds/critical.wav'
import warning from '../../../sounds/warning.wav'

import {
    close,
    getAssetRules,
    getSoundStatus,
    loadMutedWidgets,
    startPolling,
    stopPolling,
    updateSoundStatus
} from '../../actions'
import {
    CONFIGURE,
    HIGHEST_SEVERITY,
    NEW_ALARM_THRESHOLD,
    RIGHT,
    SECOND_HIGHEST_SEVERITY,
    UPDATE_RULES_ASSET,
    UPDATE_MUTED,
    VIEW,
    WIDTH,
    UPDATE_SOUND_BROADCAST
} from '../../constants'
import EditorPage from '../../editor/components'
import useDisplayUnits from '../../hooks/useDisplayUnits'
import { getHighestSeverity } from '../../utils'
import Drawer from './Drawer'
import Viewer from './Viewer'

const favicon = new Favico({ animation: 'none' })

const activeSound = new Audio(active)
const criticalSound = new Audio(critical)
const warningSound = new Audio(warning)

function mapStateToProps(state) {
    const { dashboardReducer, editorReducer } = state || {}
    const { mutedWidgets, queryStopped, widgetsWithAlarms } = dashboardReducer || {}
    const { assetId, broadcastChannel, channel, curves, isOpen, loading, soundStatus } = editorReducer || {}

    return {
        assetId,
        broadcastChannel,
        channel,
        curves,
        isOpen,
        loading,
        mutedWidgets,
        queryStopped,
        soundStatus,
        widgetsWithAlarms
    }
}

const AnimationsStyle = createGlobalStyle`
    @keyframes new-alarm {
        from {
            background: rgba(245, 64, 0, 0.08);
            box-shadow: inset 0px 0px 0px 2px rgba(245, 64, 0, 0.4);
        }

        to {
            background: rgba(245, 64, 0, 0.16);
            box-shadow: inset 0px 0px 0px 12px rgba(245, 64, 0, 0.4);
        }
    }
`

function animateDashboardBorders() {
    const dashboardContent = document.querySelector('.dashboard-content')

    dashboardContent.style.animation = 'none'

    // force DOM reflow so that the animation is executed again (when it's not the 1st time)
    // eslint-disable-next-line no-unused-expressions
    dashboardContent.offsetHeight

    dashboardContent.style.animation = '0.75s 6 alternate new-alarm'
}

export default connect(mapStateToProps, {
    close,
    getAssetRules,
    getSoundStatus,
    loadMutedWidgets,
    startPolling,
    stopPolling,
    updateSoundStatus
})(styled(function ({
    alarmsComponent,
    className,
    isOpen,
    displayUnitStore,

    assetId,
    broadcastChannel,
    channel,
    curves,
    loading,
    mutedWidgets,
    queryStopped,
    soundStatus,
    widgetsWithAlarms,

    close,
    getAssetRules,
    getSoundStatus,
    loadMutedWidgets,
    startPolling,
    stopPolling,
    updateSoundStatus
}) {
    const { loadingSelectedUnitSet, selectedUnitSet } = useDisplayUnits(displayUnitStore)

    const soundRef = useRef(null)

    const [mode, setMode] = useState(VIEW)
    const [showBanner, setShowBanner] = useState(false)
    const [side, setSide] = useState(RIGHT)

    const { state, dashboard } = useContext(DashboardContext)
    const { filterValues } = state

    // eslint-disable-next-line camelcase
    const currentAsset = filterValues?.asset_name_event_type?.[0]

    useEffect(() => {
        // workaround to allow autoplay (doesn't work every time though)
        document.body.click()
    }, [])

    useEffect(() => {
                //remove
                if(!broadcastChannel) return

        let abortController = null

        broadcastChannel.onmessage = msg => {
            if (!msg) {
                console.warn(`Broadcast channel received falsy message = ${msg}`)
            } else {
                switch (msg.type) {
                    case UPDATE_RULES_ASSET:
                        if (msg.assetId === assetId) {
                            abortController = new AbortController()
                            getAssetRules(undefined, abortController, undefined, true)
                        }
                        break
                    case UPDATE_MUTED:
                        if (msg.dashboardId === dashboard.id) {
                            loadMutedWidgets(
                                dashboard.id,
                                dashboard.widgets.map(w => w.id)
                            )
                        }
                        break
                    case UPDATE_SOUND_BROADCAST:
                        if (msg.assetId === assetId && msg.status && Object.keys(msg.status).length) {
                            updateSoundStatus(msg.status)
                        }
                        break
                    default:
                        console.warn(`Broadcast channel received message with unknown type = ${msg.type}`)
                }
            }
        }

        return () => {
            if (abortController) {
                abortController.abort()
            }
        }
    }, [dashboard.id, dashboard.widgets, broadcastChannel, getAssetRules, loadMutedWidgets, assetId, updateSoundStatus])

    useEffect(() => {
        if (isOpen && mode === CONFIGURE && (assetId || assetId === 0) && curves && !loadingSelectedUnitSet) {
            const abortController = new AbortController()
            getAssetRules(channel, abortController, selectedUnitSet)

            return () => {
                abortController.abort()
            }
        }
    }, [assetId, channel, mode, isOpen, getAssetRules, selectedUnitSet, curves, loadingSelectedUnitSet])

    useEffect(() => {
        loadMutedWidgets(
            dashboard.id,
            dashboard.widgets.map(w => w.id)
        )
    }, [dashboard.id, dashboard.widgets, loadMutedWidgets])

    useEffect(() => {
        const abortController = new AbortController()
        getSoundStatus(assetId, abortController)

        return () => {
            abortController.abort()

            if (soundRef.current) {
                clearInterval(soundRef.current)
                activeSound.pause()
                soundRef.current = null
            }
        }
    }, [assetId, dashboard.id, getSoundStatus])

    useEffect(() => {
        if (currentAsset && !loadingSelectedUnitSet) {
            const abortController = new AbortController()
            startPolling(currentAsset, selectedUnitSet?.id || null, abortController)

            return () => {
                close()
                setMode(VIEW)
                abortController.abort()
                stopPolling()
            }
        }
    }, [currentAsset, dashboard.id, selectedUnitSet?.id, loadingSelectedUnitSet, startPolling, close, stopPolling])

    useEffect(() => {
        let _connected = null
        let abortController = null

        const connectListener = window.cometd.addListener('/meta/connect', message => {
            if (window.cometd.isDisconnected()) {
                return
            }

            const wasConnected = _connected
            _connected = message.successful

            // reconnected
            if (wasConnected === false && _connected) {
                abortController = new AbortController()

                stopPolling()
                startPolling(currentAsset, selectedUnitSet?.id || null, abortController)
            }
        })

        const disconnectListener = window.cometd.addListener('/meta/disconnect', message => {
            if (message.successful) {
                _connected = false
            }
        })

        return () => {
            if (abortController) abortController.abort()

            window.cometd.removeListener(connectListener)
            window.cometd.removeListener(disconnectListener)
        }
    }, [currentAsset, selectedUnitSet?.id, startPolling, stopPolling])

    useEffect(() => {
        if (queryStopped) {
            const abortController = new AbortController()

            stopPolling()
            startPolling(currentAsset, selectedUnitSet?.id || null, abortController)

            return () => {
                abortController.abort()
            }
        }
    }, [currentAsset, queryStopped, selectedUnitSet?.id, startPolling, stopPolling])

    useEffect(() => {
        // used on cleanup
        let currentTitleNotification

        if (Object.keys(widgetsWithAlarms).length) {
            const dashboardAlarms = Object.keys(widgetsWithAlarms).reduce(
                (alarms, widgetId) => ({
                    ...alarms,
                    ...Object.keys(widgetsWithAlarms[widgetId]).reduce(
                        (widgetAlarms, channel) => ({
                            ...widgetAlarms,
                            ...widgetsWithAlarms[widgetId][channel].reduce(
                                (channelAlarms, channelAlarm) => ({
                                    ...channelAlarms,
                                    [channelAlarm.id]: channelAlarm
                                }),
                                {}
                            )
                        }),
                        {}
                    )
                }),
                {}
            )

            const now = new Date().getTime()
            const newAlarms = Object.keys(dashboardAlarms)
                .filter(
                    alarmId =>
                        dashboardAlarms[alarmId].new &&
                        Math.abs(now - dashboardAlarms[alarmId].lastUpdatedAt) <= NEW_ALARM_THRESHOLD
                )
                .map(alarmId => dashboardAlarms[alarmId])

            const count = Object.keys(dashboardAlarms).length

            if (count) {
                if (newAlarms.length) {
                    animateDashboardBorders()

                    const newAlarmsWithSound = []
                    for (const widgetId in widgetsWithAlarms) {
                        if (mutedWidgets[widgetId]) continue

                        for (const channel in widgetsWithAlarms[widgetId]) {
                            widgetsWithAlarms[widgetId][channel].forEach(alarm => {
                                if (
                                    newAlarms.find(
                                        newAlarm =>
                                            newAlarm.id === alarm.id &&
                                            newAlarm.lastUpdatedAt === alarm.lastUpdatedAt &&
                                            newAlarm.totalEvents === alarm.totalEvents
                                    )
                                ) {
                                    const ruleId = alarm.latestTrigger?.alarm?.ref?.ruleId || alarm.ref.ruleId
                                    if (soundStatus[ruleId]) {
                                        newAlarmsWithSound.push(alarm)
                                    }
                                }
                            })
                        }
                    }

                    const newAlarmsSeverity = getHighestSeverity(newAlarmsWithSound)
                    if (newAlarmsSeverity === HIGHEST_SEVERITY || newAlarmsSeverity === SECOND_HIGHEST_SEVERITY) {
                        activeSound.pause()

                        if (newAlarmsSeverity === HIGHEST_SEVERITY) {
                            criticalSound.play()
                        } else {
                            warningSound.play()
                        }
                    }
                }

                const dashboardAlarmsWithSound = []
                for (const widgetId in widgetsWithAlarms) {
                    if (mutedWidgets[widgetId]) continue

                    for (const channel in widgetsWithAlarms[widgetId]) {
                        widgetsWithAlarms[widgetId][channel].forEach(alarm => {
                            if (
                                dashboardAlarms[alarm.id] &&
                                dashboardAlarms[alarm.id].lastUpdatedAt === alarm.lastUpdatedAt &&
                                dashboardAlarms[alarm.id].totalEvents === alarm.totalEvents
                            ) {
                                const ruleId = alarm.latestTrigger?.alarm?.ref?.ruleId || alarm.ref.ruleId
                                if (soundStatus[ruleId]) {
                                    dashboardAlarmsWithSound.push(alarm)
                                }
                            }
                        })
                    }
                }

                const highestSeverity = getHighestSeverity(dashboardAlarmsWithSound)
                if (highestSeverity === HIGHEST_SEVERITY || highestSeverity === SECOND_HIGHEST_SEVERITY) {
                    if (soundRef.current === null) {
                        const now = new Date()
                        const diff = 5000 - (now.getSeconds() % 5) * 1000 - (1000 - now.getMilliseconds())

                        const fn = () => {
                            if (criticalSound.paused && warningSound.paused) {
                                activeSound
                                    .play()
                                    .then(() => setShowBanner(false))
                                    .catch(() => {
                                        setShowBanner(true)
                                        console.warn(
                                            'An alarm sound could not autoplay because sometimes the browser requires the user to interact with the document before some media can autoplay; the audio should play normally after you click on anything in the page'
                                        )
                                    })
                            }
                            soundRef.current = setTimeout(fn, 5000)
                        }

                        soundRef.current = setTimeout(fn, diff)
                    }
                } else {
                    activeSound.pause()
                    if (soundRef.current) {
                        clearInterval(soundRef.current)
                        soundRef.current = null
                    }
                }

                const newTitleNotification = {
                    title: `${count} Open Alarm${count > 1 ? 's' : ''}`,
                    priority: 1000
                }

                const severity = getHighestSeverity(Object.keys(dashboardAlarms).map(id => dashboardAlarms[id]))
                if (severity === HIGHEST_SEVERITY || severity === SECOND_HIGHEST_SEVERITY) {
                    favicon.badge(count.toLocaleString('en-US', { minimumIntegerDigits: 2 }), {
                        bgColor: severity === HIGHEST_SEVERITY ? '#ae1f1f' : '#ff9326'
                    })
                }

                LiveApi.Notification.documentTitle.register(newTitleNotification)
                currentTitleNotification = newTitleNotification
            } else if (soundRef.current) {
                activeSound.pause()
                clearInterval(soundRef.current)
                soundRef.current = null
            }
        } else if (soundRef.current) {
            activeSound.pause()
            clearInterval(soundRef.current)
            soundRef.current = null
        }

        return () => {
            if (currentTitleNotification) {
                LiveApi.Notification.documentTitle.unregister(currentTitleNotification)
            }

            favicon.reset()
        }
    }, [widgetsWithAlarms, soundStatus, mutedWidgets])

    const style = { width: isOpen ? WIDTH : 0 }

    return (
        <div style={style} className={className + ` alarms-sidebar--${side}`}>
            {ReactDOM.createPortal(
                <GlobalAlert
                    message={
                        <>
                            Your browser blocked sound from playing before an interaction. Please click anywhere to
                            allow alarms to play sound
                            <br />
                            <span className="alarm-autoplay-banner--extra">
                                {"You can also change the browser's sound settings for the page to always allow"}
                            </span>
                        </>
                    }
                    show={showBanner}
                    allowClose={true}
                    className="alert-sm info alarm-autoplay-banner"
                    visibleTime={-1}
                />,
                document.querySelector('body')
            )}

            <AnimationsStyle />

            <Drawer
                title={`Active Alarms for ${
                    channel
                        ? curves?.[channel] && curves[channel].description
                            ? curves[channel].description + ' (' + channel + ')'
                            : channel
                        : ''
                }`}
                onClose={close}
                onConfigure={() => setMode(CONFIGURE)}
                mode={mode}
                onView={() => setMode(VIEW)}
                side={side}
                setSide={setSide}
                isOpen={isOpen}
            >
                <div className={`inner${loading ? ' loading' : ''}`}>
                    {mode === VIEW ? (
                        <Viewer
                            channel={channel}
                            assets={filterValues.asset_name_event_type}
                            selectedUnitSet={selectedUnitSet}
                            AlarmsComponent={alarmsComponent}
                        />
                    ) : (
                        <EditorPage
                            channel={channel}
                            dashboardUnitSet={selectedUnitSet}
                            loadingSelectedUnitSet={loadingSelectedUnitSet}
                            selected={{ asset: { id: assetId } }}
                            sideview={true}
                        />
                    )}
                </div>
            </Drawer>
        </div>
    )
})`
    border: 1px solid #ccc;
    position: absolute;
    z-index: 1000;
    top: 0;
    bottom: 0px;
    background: #fff;

    .alarm-rules-editor--title {
        font-family: SourceSansPro-Light;
        font-size: 16px;
        font-style: italic;
        color: #364159;
        margin-bottom: 12px;
    }

    &.alarms-sidebar--left {
        left: 0;
    }
    &.alarms-sidebar--right {
        right: 0;
    }

    .alarms-config-link {
        display: flex;
        height: 200px;
        align-items: center;
        justify-content: center;
        font-size: 16px;
    }
`)
